import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Grid, Alert, CircularProgress, Chip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  UploadFile as UploadFileIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { tcEmployeeApi } from './employeeApi';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { MAX_FILE_BYTES, MAX_FILE_LABEL, oversizeMessage } from '../../utils/fileSizeLimit';
import LocalFilePreview from './LocalFilePreview';

const EMPTY = { name: '', passportNumber: '', passportIssueDate: '', passportExpiryDate: '', emiratesId: '', phone: '', email: '' };

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return (!s || s.toLowerCase() === 'null') ? '' : s;
};

const toIsoDate = (v) => {
  const s = clean(v);
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

/**
 * Add an employee to a CLIENT customer from the Employees tab. Optionally drop a passport scan
 * to auto-fill the fields via OCR; that scan is then attached as the employee's passport copy.
 */
const AddClientEmployeeDialog = ({ open, onClose, orgId, onCreated, api: apiProp }) => {
  // Typing-center calls by default; the portal passes its own (employeeApi.js).
  const api = apiProp || tcEmployeeApi(orgId);
  const [form, setForm] = useState(EMPTY);
  const [passportFile, setPassportFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setForm(EMPTY);
    setPassportFile(null);
    setExtracted(null);
    setExtracting(false);
    setExtractNote('');
    setSaving(false);
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handlePassportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError(oversizeMessage(file));
      return;
    }
    setError('');
    setPassportFile(file);
    setExtracted(null);
    setExtractNote('');
    setExtracting(true);
    try {
      const r = await api.extract(file, 'passport_copy');
      if (r?.data?.success === false) {
        setExtractNote(r.data.error || r.data.message || 'Automatic extraction failed. Fill the fields manually.');
        return;
      }
      const data = r?.data?.data || {};
      if (data._extractionSkipped) {
        setExtractNote(data._extractionSkippedReason || 'Automatic extraction was skipped. Fill the fields manually.');
        return;
      }
      setExtracted(data);
      const fullName = clean(data.fullName) || [clean(data.firstName), clean(data.middleName), clean(data.lastName)].filter(Boolean).join(' ');
      const passport = resolveExtractedDocNumber(data, 'passport_copy');
      setForm((prev) => ({
        ...prev,
        name: prev.name || fullName,
        passportNumber: prev.passportNumber || passport,
        passportIssueDate: prev.passportIssueDate || toIsoDate(data.issueDate),
        passportExpiryDate: prev.passportExpiryDate || toIsoDate(data.expiryDate)
      }));
    } catch (err) {
      setExtractNote('Automatic extraction failed. Fill the fields manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    const passport = form.passportNumber.trim();
    const eid = form.emiratesId.trim();
    if (!passport && !eid) {
      setError('Enter a passport number or an Emirates ID.');
      return;
    }
    setSaving(true);
    try {
      const resp = await api.create({
        name: form.name.trim() || null,
        passport_number: passport || null,
        emirates_id: eid || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null
      });
      const result = resp?.data?.data;
      if (!result?.uid) throw new Error(resp?.data?.error || 'No employee uid returned');

      let passportUploadError = null;
      if (passportFile) {
        try {
          await api.upload(result.uid, [passportFile], 'passport_copy', {
            documentNumber: passport || null,
            issueDate: form.passportIssueDate || null,
            expiryDate: form.passportExpiryDate || null
          });
        } catch (upErr) {
          passportUploadError = upErr?.response?.data?.error || 'The employee was added but the passport copy could not be uploaded.';
        }
      }
      onCreated({ ...result, passportUploadError });
      reset();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAddIcon color="secondary" />
        Add Employee
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={extracting || saving}>
              {passportFile ? 'Replace passport scan' : 'Scan passport (optional)'}
              <input type="file" hidden accept="image/*,.pdf" onChange={handlePassportFile} />
            </Button>
            {extracting && <CircularProgress size={18} />}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Fields are filled from the scan and the file is attached as the passport copy. Max {MAX_FILE_LABEL}.
          </Typography>
          {passportFile && (
            <Box sx={{ mt: 1 }}>
              <LocalFilePreview
                file={passportFile}
                defaultExpanded
                disabled={saving || extracting}
                badge={extracted ? (
                  <Chip size="small" icon={<CheckIcon />} color="success" label="Extracted" sx={{ height: 20, fontSize: '0.7rem' }} />
                ) : null}
                onRemove={() => {
                  setPassportFile(null); setExtracted(null); setExtractNote('');
                  setForm((prev) => ({ ...prev, passportIssueDate: '', passportExpiryDate: '' }));
                }}
              />
            </Box>
          )}
          {extractNote && <Alert severity="warning" sx={{ mt: 1 }}>{extractNote}</Alert>}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Full name" value={form.name} onChange={setField('name')} fullWidth size="small" autoFocus />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Passport number" value={form.passportNumber} onChange={setField('passportNumber')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Emirates ID" value={form.emiratesId} onChange={setField('emiratesId')} fullWidth size="small" />
          </Grid>
          {passportFile && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField label="Passport issue date" type="date" value={form.passportIssueDate} InputLabelProps={{ shrink: true }}
                  onChange={setField('passportIssueDate')} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Passport expiry date" type="date" value={form.passportExpiryDate} InputLabelProps={{ shrink: true }}
                  onChange={setField('passportExpiryDate')} fullWidth size="small" />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" value={form.phone} onChange={setField('phone')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" type="email" value={form.email} onChange={setField('email')} fullWidth size="small" />
          </Grid>
        </Grid>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
          A passport number or Emirates ID is required. If a matching employee already exists in Thaklis, it is reused.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || extracting}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Add Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClientEmployeeDialog;
